import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Boxes,
  Eye,
  LayoutDashboard,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import GlassBackground from "@/shared/components/glassBackground";
import { confirmDialog } from "@/shared/components/dialog";
import { toast } from "@/shared/components/toast";
import { useAutomations } from "@/features/automations/hooks/useAutomations";
import { useWorkflowExecution } from "@/features/automations/hooks/useWorkflowExecution";
import { AutomationsGrid } from "@/features/automations/components/automations_grid";
import ShareAutomationModal from "@/features/automations/components/share_automation_modal";
import type AutomationSummary from "@/features/automations/models/AutomationSummary";
import { useWorkspaces } from "../hooks/useWorkspaces";
import type { WorkspaceDetail, WorkspaceUpsert } from "../models/Workspace";
import WorkspaceFormModal from "../components/workspace_form_modal";
import AssignWorkflowModal from "../components/assign_workflow_modal";

const SECONDARY_BUTTON =
  "flex items-center gap-2 h-9 px-4 rounded-xl border border-black/5 bg-white/60 text-sm font-semibold text-foreground/70 hover:bg-white hover:text-accent hover:shadow-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed";

export default function WorkspaceDetailPage() {
  const { workspaceId = "" } = useParams();
  const navigate = useNavigate();
  const { loading, error, get, update, remove, assignWorkflow, removeWorkflow } = useWorkspaces();
  const { remove: removeAutomation } = useAutomations();
  const { run } = useWorkflowExecution();

  const [workspace, setWorkspace] = useState<WorkspaceDetail | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runningWorkflowId, setRunningWorkflowId] = useState<string | null>(null);
  const [deletingWorkflowId, setDeletingWorkflowId] = useState<string | null>(null);
  const [sharingAutomation, setSharingAutomation] = useState<AutomationSummary | null>(null);

  /** Recarga usada por los modales tras archivar/quitar una automatización. */
  const reload = useCallback(async () => {
    const detail = await get(workspaceId);
    if (detail) setWorkspace(detail);
  }, [get, workspaceId]);

  useEffect(() => {
    let ignore = false;
    const fetch = async () => {
      const detail = await get(workspaceId);
      if (!ignore && detail) setWorkspace(detail);
    };
    fetch();
    return () => {
      ignore = true;
    };
  }, [get, workspaceId]);

  const isOwner = workspace?.isOwner ?? false;
  const automations = workspace?.workflows ?? [];

  const handleSave = async (upsert: WorkspaceUpsert) => {
    setSaving(true);
    try {
      const saved = await update(workspaceId, upsert);
      if (saved) {
        setWorkspace((prev) => (prev ? { ...prev, ...saved } : prev));
        setFormOpen(false);
      }
    } catch {
      // useAsync ya notificó el error vía toast
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWorkspace = async () => {
    const confirmed = await confirmDialog({
      title: "Eliminar workspace",
      message: `Se eliminará "${workspace?.name}" y su dashboard. Las automatizaciones que agrupa NO se eliminan: quedan sin agrupar.`,
      confirmLabel: "Eliminar",
      cancelLabel: "Cancelar",
      variant: "danger",
    });
    if (!confirmed) return;
    await remove(workspaceId);
    navigate("/admin/workspaces");
  };

  const handleRun = async (workflowId: string) => {
    setRunningWorkflowId(workflowId);
    try {
      const result = await run(workflowId);
      if (result.status === "Completed") {
        toast.success("Ejecución completada correctamente");
      } else if (result.status === "Failed") {
        toast.error("La ejecución finalizó con error");
      } else {
        toast.warning(`La ejecución finalizó con estado: ${result.status}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al ejecutar la automatización");
    } finally {
      setRunningWorkflowId(null);
    }
  };

  const handleDeleteAutomation = async (automation: AutomationSummary) => {
    const confirmed = await confirmDialog({
      title: "Eliminar automatización",
      message: `Se eliminará "${automation.name}" con todas sus versiones y su configuración (webhook, programación, estado). El historial de ejecuciones se conserva. Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar",
      cancelLabel: "Cancelar",
      variant: "danger",
    });
    if (!confirmed) return;

    setDeletingWorkflowId(automation.workflowId);
    try {
      await removeAutomation(automation.workflowId);
      setWorkspace((prev) =>
        prev
          ? { ...prev, workflows: prev.workflows.filter((a) => a.workflowId !== automation.workflowId) }
          : prev,
      );
    } catch {
      // useAsync ya notificó el error vía toast
    } finally {
      setDeletingWorkflowId(null);
    }
  };

  /** Sacarla del workspace no la elimina: vuelve a la lista general, sin agrupar. */
  const handleUnfile = async (automation: AutomationSummary) => {
    await removeWorkflow(workspaceId, automation.workflowId);
    setWorkspace((prev) =>
      prev
        ? { ...prev, workflows: prev.workflows.filter((a) => a.workflowId !== automation.workflowId) }
        : prev,
    );
  };

  return (
    <GlassBackground>
      <div className="flex h-full w-full flex-col">
        <header className="flex items-center gap-3 border-b border-black/5 bg-white/70 px-4 py-2.5 backdrop-blur-md">
          <button
            onClick={() => navigate("/admin/workspaces")}
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-black/5 bg-white/60 text-foreground/60 transition-all hover:bg-white hover:text-accent hover:shadow-sm"
            title="Volver a workspaces"
          >
            <ArrowLeft className="size-4" />
          </button>

          <div
            className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-accent text-white"
            style={workspace?.color ? { backgroundColor: workspace.color } : undefined}
          >
            {workspace?.icon && workspace.icon.length <= 4 && !workspace.icon.includes("/") ? (
              <span className="text-lg leading-none">{workspace.icon}</span>
            ) : workspace?.icon ? (
              <img src={workspace.icon} alt="" className="size-full object-cover" />
            ) : (
              <Boxes className="size-4" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-bold text-foreground">
              {workspace?.name || "Workspace"}
            </h1>
            {workspace?.description && (
              <p className="truncate text-[11px] text-foreground/40">{workspace.description}</p>
            )}
          </div>

          {workspace && !isOwner && (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/10 px-3 py-1.5 text-[11px] font-bold text-amber-600">
              <Eye className="size-3.5" />
              SOLO LECTURA
            </span>
          )}

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <button
              onClick={() => navigate(`/admin/workspaces/${workspaceId}/dashboard`)}
              className={SECONDARY_BUTTON}
            >
              <LayoutDashboard className="size-4" />
              Dashboard
            </button>
            {isOwner && (
              <>
                <button onClick={() => setAssignOpen(true)} className={SECONDARY_BUTTON}>
                  <Plus className="size-4" />
                  Agregar automatizaciones
                </button>
                <button
                  onClick={() => setFormOpen(true)}
                  className={SECONDARY_BUTTON}
                  title="Editar workspace"
                >
                  <Pencil className="size-4" />
                </button>
                <button
                  onClick={() => void handleDeleteWorkspace()}
                  className={SECONDARY_BUTTON}
                  title="Eliminar workspace"
                >
                  <Trash2 className="size-4" />
                </button>
              </>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-auto p-8">
          <AutomationsGrid
            automations={automations}
            loading={loading && !workspace}
            error={error}
            runningWorkflowId={runningWorkflowId}
            deletingWorkflowId={deletingWorkflowId}
            emptyMessage={
              isOwner
                ? "Este workspace todavía no agrupa ninguna automatización. Archivá acá las de esta aplicación para verlas juntas y medirlas en un solo dashboard."
                : "Este workspace todavía no agrupa ninguna automatización que puedas ver."
            }
            emptyAction={
              isOwner
                ? { label: "Agregar automatizaciones", onClick: () => setAssignOpen(true) }
                : undefined
            }
            onEdit={(workflowId) => navigate(`/admin/automations/${workflowId}`)}
            onRun={handleRun}
            onDelete={handleDeleteAutomation}
            onShare={setSharingAutomation}
            onUnfile={isOwner ? handleUnfile : undefined}
          />
        </main>
      </div>

      {formOpen && workspace && (
        <WorkspaceFormModal
          workspace={{
            id: workspace.id,
            name: workspace.name,
            description: workspace.description,
            icon: workspace.icon,
            color: workspace.color,
            workflowCount: workspace.workflows.length,
            hasDashboard: workspace.hasDashboard,
            isOwner: workspace.isOwner,
            updatedAt: workspace.updatedAt,
          }}
          saving={saving}
          onSave={handleSave}
          onClose={() => setFormOpen(false)}
        />
      )}

      {assignOpen && workspace && (
        <AssignWorkflowModal
          workspaceId={workspaceId}
          workspaceName={workspace.name}
          assignedWorkflowIds={automations.map((a) => a.workflowId)}
          onAssign={async (workflowId) => {
            await assignWorkflow(workspaceId, workflowId);
            await reload();
          }}
          onRemove={async (workflowId) => {
            await removeWorkflow(workspaceId, workflowId);
            await reload();
          }}
          onClose={() => setAssignOpen(false)}
        />
      )}

      {sharingAutomation && (
        <ShareAutomationModal
          automation={sharingAutomation}
          onClose={() => setSharingAutomation(null)}
        />
      )}
    </GlassBackground>
  );
}
